import type { KeyboardEvent } from 'react'
import { ACTAS_ROSTER } from '../data/roster-config'
import type { BlockCode } from '../engine/types'
import { nextBlockIndex } from './block-picker-keys'

export interface BlockPickerProps {
  label: string
  value: BlockCode | null
  onChange: (block: BlockCode) => void
}

/** The eight blocks, in the roster's own order — `1A` first, `4B` last. */
const BLOCK_CODES: readonly BlockCode[] = ACTAS_ROSTER.blockOrder

/**
 * A 2-column × 4-row grid: `1A|1B` / `2A|2B` / `3A|3B` / `4A|4B`.
 *
 * Not `SegmentedControl` — that lays its options out in a single row
 * (`grid-auto-flow: column`), and eight options at 390px works out around
 * 37px each after gaps, under the 44px touch target every other control in
 * this system obeys. Two columns gives roughly 170px targets, and mirrors
 * how the roster itself names the blocks — `1A`/`1B` are a pair, same as
 * `2A`/`2B` and the rest.
 *
 * Otherwise this copies `SegmentedControl`'s mechanics exactly, because a
 * second radiogroup with different keyboard behaviour would be a trap for
 * anyone who has already learned the first one:
 *
 * - `role="radiogroup"` with a required `aria-label`, inside `.sl-field`
 *   with an `.sl-label` naming it visibly too.
 * - Each option is a `<button role="radio" aria-checked={…}>`.
 * - Selection styling is the attribute selector `[aria-checked='true']`,
 *   never a state class — the house convention throughout `components.css`.
 * - Selection is carried by fill *and* font-weight, never colour alone.
 * - One roving tab stop: `tabIndex={index === (selected === -1 ? 0 :
 *   selected) ? 0 : -1}` — with nothing chosen yet, option 0 is the way in.
 * - Selection follows focus on every arrow key, same as `SegmentedControl`:
 *   this picker only changes what is on screen inside the confirmation
 *   flow, not what is saved, so switching is cheap and reversible.
 *
 * The one real difference is the arithmetic behind the arrow keys —
 * `nextBlockIndex` in `./block-picker-keys.ts`, because this is a grid and
 * `src/ui/roving.ts`'s `nextIndex` only knows a single row.
 *
 * Focus is moved by querying the group at keypress time
 * (`closest('[role="radiogroup"]')` then `querySelectorAll('[role="radio"]')`)
 * rather than by holding a ref per option — `roving.ts`'s own comment on
 * `rovingKeyDown` explains why: one query per keypress beats a ref array
 * rebuilt on every render.
 */
export function BlockPicker({ label, value, onChange }: BlockPickerProps) {
  const selected = value === null ? -1 : BLOCK_CODES.indexOf(value)

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const target = nextBlockIndex(event.key, selected, BLOCK_CODES.length, 2)
    if (target === null) return

    event.preventDefault()

    const group = event.currentTarget.closest('[role="radiogroup"]')
    const options = group?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
    options?.item(target)?.focus()

    onChange(BLOCK_CODES[target])
  }

  return (
    <div className="sl-field">
      <span className="sl-label">{label}</span>
      <div role="radiogroup" aria-label={label} className="ss-block-picker">
        {BLOCK_CODES.map((code, index) => (
          <button
            key={code}
            type="button"
            role="radio"
            aria-checked={code === value}
            // Nothing selected yet leaves the first option as the way in,
            // rather than a group nothing can reach.
            tabIndex={index === (selected === -1 ? 0 : selected) ? 0 : -1}
            className="ss-block-picker__option"
            onClick={() => onChange(code)}
            onKeyDown={handleKeyDown}
          >
            {code}
          </button>
        ))}
      </div>
    </div>
  )
}
